export type PersonaSection = {
  heading: string;
  icon: string;
  content: string[];
};

export type Persona = {
  slug: string;
  persona: string;
  metaTitle: string;
  metaDescription: string;
  heroTitle: string;
  heroSubtitle: string;
  sections: PersonaSection[];
  quickTips: string[];
  ctaText: string;
};

export const personas: Persona[] = [
  {
    slug: "beginners",
    persona: "Vinyl Beginners",
    metaTitle: "Vinyl for Beginners: How to Start Your Record Collection in 2026",
    metaDescription:
      "New to vinyl? Everything you need to know to start your record collection — equipment, first records to buy, where to shop, and common mistakes to avoid.",
    heroTitle: "New to Vinyl?",
    heroSubtitle:
      "You don't need to be an audiophile or spend a fortune. Here's everything a beginner needs to know to start building a vinyl collection that brings real joy.",
    sections: [
      {
        heading: "What Equipment Do You Actually Need?",
        icon: "🎛️",
        content: [
          "The barrier to entry is lower than you think. You need three things: a turntable, an amplifier (or powered speakers with a built-in amp), and speakers. That's it. Many entry-level turntables include a built-in phono preamp, simplifying the chain further.",
          "Recommended starter setup: Audio-Technica AT-LP60X turntable ($130) + Edifier R1280T powered speakers ($100). Total: $230 for a setup that sounds genuinely great. As you grow into the hobby, you can upgrade individual components.",
          "Avoid suitcase-style record players under $60 — they use heavy tracking forces that can damage vinyl over time. It's better to spend a little more on something that protects your investment in records.",
        ],
      },
      {
        heading: "Your First 10 Records",
        icon: "💿",
        content: [
          "Don't overthink your first purchases. Start with albums you already love from streaming — music you know you'll actually sit down and listen to. GoOffline makes this easy: paste your favorite Spotify playlist and get a shopping list of vinyl ready to buy.",
          "Mix new releases (widely available, guaranteed quality) with a few used records from a local shop (affordable, and crate-digging is half the fun). Having a mix teaches you to appreciate different pressing qualities and eras.",
          "Pro tip: Buy one album you've never heard based purely on the cover art. Vinyl rewards spontaneous discovery — you might find your new favorite artist in the bargain bin.",
        ],
      },
      {
        heading: "Where to Buy",
        icon: "🏪",
        content: [
          "Local record stores are your best friend as a beginner. Staff can recommend pressings, help you avoid overpriced reissues, and introduce you to your local music community. Support them — they're invaluable resources.",
          "Online options include Discogs (the world's largest record marketplace), Amazon (convenient for new releases), and specialty stores like CD&LP.com. For used records, thrift stores and estate sales can yield incredible finds at rock-bottom prices.",
          "Avoid: paying scalper prices for limited editions, buying from random eBay sellers without checking their ratings, and assuming older always means better. A well-mastered modern pressing often sounds better than a worn vintage copy.",
        ],
      },
      {
        heading: "Common Beginner Mistakes",
        icon: "⚠️",
        content: [
          "Stacking records flat — always store vinyl vertically, like books. Horizontal stacking causes warping from the weight. Use a shelf, crate, or dedicated record storage furniture.",
          "Touching the grooves — always handle records by the edges and the label. Fingerprints leave oils that attract dust and degrade sound quality. Wash your hands before handling if possible.",
          "Skipping cleaning — even new records benefit from a quick brush before playing. Invest in a carbon fiber anti-static brush ($15) and use it before every play. For used records, a wet cleaning kit ($20-30) makes a dramatic difference.",
          "Buying for investment instead of enjoyment — buy music you love first. Some records appreciate in value, but collecting should be driven by passion for the music, not speculation.",
        ],
      },
    ],
    quickTips: [
      "Store records vertically, never flat or leaning",
      "Use anti-static inner sleeves ($15 for 50)",
      "Brush records before every play",
      "Replace your stylus every 500-1,000 hours",
      "Start with albums you already love",
      "Join r/vinyl for community tips and support",
    ],
    ctaText:
      "Paste your Spotify playlist into GoOffline and discover which albums to buy first — with prices and links.",
  },
  {
    slug: "audiophiles",
    persona: "Audiophiles",
    metaTitle: "Vinyl for Audiophiles: Mastering-Quality Pressings & Sound Optimization",
    metaDescription:
      "Audiophile guide to vinyl — best mastering engineers, pressing plants, cartridge selection, room optimization, and building a reference-quality vinyl collection.",
    heroTitle: "Vinyl for Audiophiles",
    heroSubtitle:
      "You care about every link in the audio chain. Here's how to optimize your vinyl experience for the highest fidelity — from master tape to ear drum.",
    sections: [
      {
        heading: "The Mastering Chain Matters Most",
        icon: "🎚️",
        content: [
          "The most impactful factor in how a record sounds isn't the vinyl weight or the color — it's the mastering chain. Specifically: what source was used (original analog tapes vs digital master), who did the mastering, and at which facility the lacquer was cut.",
          "Gold-standard mastering engineers for vinyl: Kevin Gray (Cohearent Audio), Bernie Grundman (Grundman Mastering), Ryan Smith (Sterling Sound), Bob Ludwig (Gateway Mastering), and Chris Bellman (Bernie Grundman Mastering). When these names appear in the credits or dead wax, you're getting a high-quality cut.",
          "All-analog mastering (AAA) — where the original analog tapes are used to cut a lacquer without any digital conversion — is the audiophile ideal. However, excellent high-resolution digital intermediaries (DSD or 96kHz/24-bit+) can yield superb results. What matters is the skill of the engineer and the quality of the source.",
        ],
      },
      {
        heading: "Pressing Plant Selection",
        icon: "🏭",
        content: [
          "The pressing plant determines the physical quality of the vinyl: surface noise, centering, non-fill, and overall consistency. Top-tier plants include Quality Record Pressings (QRP) in Kansas, Optimal Media in Germany, Pallas in Germany, and Record Industry in the Netherlands.",
          "Quality Record Pressings, founded by Acoustic Sounds' Chad Kassem, is widely considered the finest pressing plant operating today. Their attention to quality control — hand-inspecting every disc — sets the standard. If a release is pressed at QRP, you can be confident in the physical quality.",
          "Budget pressings from overloaded plants may exhibit non-fill (grooves not fully formed), off-center pressing, surface noise from recycled vinyl, and other quality issues. When researching a purchase, check Discogs and Steve Hoffman Forums for pressing plant information.",
        ],
      },
      {
        heading: "Cartridge & Stylus Optimization",
        icon: "💎",
        content: [
          "Your cartridge and stylus profile fundamentally shape your vinyl's sound. Moving magnet (MM) cartridges are affordable and excellent for most listeners. Moving coil (MC) cartridges offer superior detail retrieval but require a dedicated MC phono preamp and cost significantly more.",
          "Stylus profiles matter: conical tips are forgiving but read less groove detail. Elliptical tips offer the best balance of detail and forgiveness. Advanced profiles (Shibata, MicroLine, SAS) extract maximum information from the groove but require precise alignment. For most audiophile setups, an elliptical or Shibata stylus on a quality cartridge (Ortofon 2M Blue, Audio-Technica VM540ML, Nagaoka MP-200) is the sweet spot.",
          "Proper cartridge alignment is non-negotiable. Use a protractor (Baerwald or Stevenson geometry), set tracking force with a digital scale, and adjust anti-skate. Misalignment causes distortion, uneven channel balance, and accelerated record wear.",
        ],
      },
      {
        heading: "Building a Reference Collection",
        icon: "📚",
        content: [
          "Audiophile labels that consistently deliver reference-quality vinyl: Mobile Fidelity Sound Lab (MoFi), Analogue Productions, Blue Note's Tone Poet & Classic Vinyl series, Speakers Corner, Craft Recordings, and Impex Records. These labels invest in original tape sources, top mastering engineers, and premium pressing plants.",
          "Build your collection around albums you love in their best available pressing. Use resources like Discogs, Steve Hoffman Forums, and dedicated audiophile review sites to identify the best pressing of each album. Sometimes it's a modern reissue; sometimes it's an original pressing. Research before purchasing.",
          "GoOffline helps audiophiles identify which albums from their streaming playlists to prioritize for vinyl purchase — then you can research the best pressing of each title.",
        ],
      },
    ],
    quickTips: [
      "Always check who mastered the lacquer (dead wax initials)",
      "Invest in cartridge alignment tools and a digital tracking force scale",
      "Clean records with a vacuum or ultrasonic cleaning machine",
      "Use quality anti-static inner sleeves (MoFi, Sleeve City)",
      "Consider room treatment — even basic absorption panels help",
      "Track your collection on Discogs with pressing-specific detail",
    ],
    ctaText:
      "Identify which albums from your streaming library deserve the audiophile vinyl treatment. Paste your playlist into GoOffline.",
  },
  {
    slug: "spotify-users",
    persona: "Spotify Users",
    metaTitle: "From Spotify to Vinyl: A Streaming Listener's Guide to Going Offline",
    metaDescription:
      "Love Spotify but want to own your music? Here's how to transition from streaming to collecting vinyl & CDs — starting with the playlists you already love.",
    heroTitle: "From Spotify to Vinyl",
    heroSubtitle:
      "You've curated years of playlists and saved albums. Now it's time to actually own the music you love. The transition is easier (and more affordable) than you think.",
    sections: [
      {
        heading: "You Already Know What to Buy",
        icon: "🎯",
        content: [
          "Here's the secret: your Spotify listening history IS your shopping list. The albums you've played 50+ times, the playlists you return to every week, the songs that score your life's moments — those are the records that deserve to live on your shelf.",
          "GoOffline was built exactly for this transition. Paste any Spotify playlist URL, and you'll get a curated list of the vinyl and CDs you need — with current prices from Discogs and direct buy links. No research needed; your streaming data does the work.",
          "Start small. Pick your top 5-10 most-played albums. That's your starter collection. Add a few more each month, and within a year you'll have a meaningful physical library of the music that matters most to you.",
        ],
      },
      {
        heading: "Why Going Offline Makes Sense",
        icon: "💡",
        content: [
          "You're currently paying ~$12/month for permission to listen. Cancel Spotify and everything vanishes — every playlist, every saved album, every year of curation. That's not ownership; it's rental with excellent UX.",
          "When you buy a vinyl record or CD, you own it permanently. No monthly fees. No licensing changes. No company can take it away. Plus, physical music sounds different — vinyl's analog warmth and the ritual of playing a record create an experience that a Spotify play button can't match.",
          "The math works too. Ten years of Spotify Premium costs ~$1,440. That's 40-50 vinyl records or 100+ CDs you'd own forever. After 10 years of Spotify, you own nothing. After buying 50 records, you own 50 records — and some may have appreciated in value.",
        ],
      },
      {
        heading: "The Hybrid Approach",
        icon: "🔄",
        content: [
          "Nobody's saying delete Spotify. The smart play is hybrid: keep streaming for discovery and casual listening, but start buying physical copies of the music that truly matters. Use Spotify as your discovery engine; use physical media as your ownership layer.",
          "Think of it like this: you might stream 10,000 songs a year, but only 50-100 albums truly resonate. Those 50-100 albums are your buying list. Everything else stays in the stream.",
          "Over time, you'll find that the albums you own physically get more of your attention. There's a psychological shift that happens when you've invested in a record — you listen more carefully, more intentionally, and get more from the experience.",
        ],
      },
      {
        heading: "Getting Started Is Easy",
        icon: "🚀",
        content: [
          "Step 1: Go to GoOffline and paste your most-played Spotify playlist. You'll get a list of albums with vinyl and CD prices.",
          "Step 2: Pick 5-10 albums you absolutely love. Check if any are available at a local record store (supporting local is awesome).",
          "Step 3: If you don't have a turntable, start with CDs — they're cheaper ($8-15 new, $1-5 used) and can be ripped to lossless FLAC files. Or invest in an entry-level turntable setup ($200-350) and go straight to vinyl.",
          "Step 4: Play your first physical album from start to finish. No skipping, no shuffle. Just you and the music. That experience is why millions of people are going offline.",
        ],
      },
    ],
    quickTips: [
      "Start with your Spotify top songs/albums — you already know you love them",
      "CDs are the budget-friendly entry point ($1-5 used)",
      "Don't buy everything at once — build gradually",
      "Keep Spotify for discovery, buy physical for ownership",
      "Join vinyl communities on Reddit (r/vinyl) for support",
      "Rip CDs to FLAC for a personal lossless streaming server",
    ],
    ctaText:
      "Paste your favorite Spotify playlist and see what it costs to own the music you love. Free, instant, no signup.",
  },
  {
    slug: "collectors",
    persona: "Music Collectors",
    metaTitle: "Physical Music Collecting: Building a Vinyl & CD Collection That Lasts",
    metaDescription:
      "Expert tips for building a serious music collection — from grading and storage to finding rare pressings and tracking market values on Discogs.",
    heroTitle: "For Serious Collectors",
    heroSubtitle:
      "Whether you're 50 records deep or 5,000, building a lasting collection is an art. Here's how experienced collectors source, grade, store, and curate for the long term.",
    sections: [
      {
        heading: "Sourcing Strategy",
        icon: "🔍",
        content: [
          "Diversify your sources. Local record stores for curated selection and knowledgeable staff. Discogs for specific pressings and competitive pricing. Estate sales for bulk finds at below-market prices. Record fairs for rare items and face-to-face negotiation. Online auctions for high-value pieces.",
          "Develop relationships with dealers who specialize in your genres. They'll set aside records matching your wants and give you first access to fresh inventory. This is especially valuable for rare and out-of-print titles that sell within hours of being listed.",
          "Set price alerts on Discogs for your wantlist items. The platform notifies you when items matching your criteria become available, and you can filter by condition, pressing, and price range. Patience rewards — that elusive pressing will surface eventually.",
        ],
      },
      {
        heading: "Grading & Authentication",
        icon: "🔬",
        content: [
          "Learn the Goldmine grading standard thoroughly. Practice grading your own collection before buying expensive records online. The gap between VG+ and NM is significant in both price and listening experience — and sellers sometimes overgrade.",
          "For high-value purchases (>$50), always request detailed photos of the record surface, label, and dead wax. Check matrix numbers against Discogs entries to verify the pressing. For very expensive items ($200+), consider requesting a play-grade from the seller.",
          "Build your knowledge of dead wax matrix codes for your collected genres. Understanding pressing information — who mastered it, where it was pressed, which stamper generation — separates informed collectors from casual buyers and protects against counterfeits.",
        ],
      },
      {
        heading: "Storage & Preservation",
        icon: "📦",
        content: [
          "Invest in proper storage: MoFi-style anti-static inner sleeves, polypropylene outer sleeves, and sturdy shelving that supports vertical storage. Temperature should be 60-75°F (15-24°C) with moderate humidity (40-50%). Avoid attics, basements, and garages.",
          "For valuable records, consider archival-quality storage: acid-free inner sleeves, resealable outer sleeves, and dedicated record cabinets. Store OBI strips, inserts, and hype stickers separately or within the outer sleeve to prevent ring wear on the cover.",
          "Catalog everything on Discogs with pressing-specific detail. This serves as your insurance record, tracks your collection's value, and helps you avoid buying duplicates. Export your collection periodically as a backup.",
        ],
      },
      {
        heading: "Expanding Strategically",
        icon: "📈",
        content: [
          "Define your collecting focus. Genre-deep (every notable jazz pressing)? Artist-complete (every Radiohead release, every pressing)? Era-focused (1970s UK prog originals)? Format-focused (Japanese OBI pressings)? A focused collection has more coherence and often better investment potential than random accumulation.",
          "Use your streaming data to identify gaps. GoOffline converts your Spotify playlists into a list of physical albums — cross-reference against your Discogs collection to find what you're missing. It's the fastest way to identify expansion opportunities.",
          "Set a monthly budget and stick to it. Collecting is a marathon, not a sprint. A consistent $50-100/month builds a substantial collection over years while keeping the hobby sustainable and the thrill of the hunt alive.",
        ],
      },
    ],
    quickTips: [
      "Grade conservatively when selling — your reputation is everything",
      "Always check matrix numbers before buying expensive pressings",
      "Invest in a record cleaning machine (Spin-Clean is great for $80)",
      "Track your collection value on Discogs — export annually",
      "Network with other collectors in your genre niche",
      "Don't chase hype — buy what you love, and value will follow",
    ],
    ctaText:
      "Find the albums missing from your collection. Paste your Spotify playlist and cross-reference with what you already own.",
  },
  {
    slug: "gift-shoppers",
    persona: "Gift Shoppers",
    metaTitle: "Vinyl & CD Gift Guide: Perfect Music Gifts for Every Budget",
    metaDescription:
      "Looking for the perfect gift for a music lover? Vinyl records and CDs make unforgettable presents. Find the right album, format, and budget for any occasion.",
    heroTitle: "The Perfect Music Gift",
    heroSubtitle:
      "A vinyl record or CD is one of the most personal, thoughtful gifts you can give. Here's how to pick the perfect album, format, and presentation for the music lover in your life.",
    sections: [
      {
        heading: "Why Physical Music Makes an Incredible Gift",
        icon: "🎁",
        content: [
          "In a world of digital gift cards and streaming subscriptions, a vinyl record stands out. It's tangible, personal, and permanent. Every time the recipient plays it, they think of you. It's a gift that literally keeps giving — for decades.",
          "Physical music also shows genuine thought. You considered what the person loves, selected a specific album, and presented something they can hold. In an age of frictionless digital consumption, that effort communicates real care.",
          "Plus, vinyl and CDs work for every budget. A meaningful used CD costs $5-10. A new vinyl record runs $25-35. A premium box set or audiophile pressing can be a $50-200 special occasion gift. There's an option for every relationship and occasion.",
        ],
      },
      {
        heading: "How to Choose the Right Album",
        icon: "🎵",
        content: [
          "The easiest approach: check their Spotify profile. If their listening is public, you can see their top artists and playlists. Paste a playlist into GoOffline and you'll see exactly which albums they love — with vinyl and CD prices and buy links.",
          "If you can't check their streaming: think about what concerts they've been to, what artist t-shirts they wear, what music they play in the car, or what era they gravitate toward. A safe bet is a classic album from their favorite genre — something they'd be proud to own and display.",
          "When in doubt, ask a friend who knows their taste, or visit a local record store and describe the person's music taste. Record store staff are exceptional at recommending albums — it's literally their passion.",
        ],
      },
      {
        heading: "Format Selection",
        icon: "📀",
        content: [
          "Vinyl is the premium gift choice. The large artwork, the ritual of playing, and the visual shelf presence make it feel special. For someone who already has a turntable, vinyl is always the right call. For someone who doesn't, a record paired with a gift card toward a turntable is a clever combo.",
          "CDs are the practical and affordable choice. They're perfect for car listeners, people with CD players, or anyone building a music library. CDs also come in beautiful packaging — deluxe editions, digipaks, and special editions can be very gift-worthy.",
          "Special editions and colored vinyl make particularly impressive gifts. A limited-edition pressing on colored vinyl feels exclusive and collectible. Check the artist's official store and Discogs for special variants.",
        ],
      },
      {
        heading: "Presentation Tips",
        icon: "✨",
        content: [
          "Vinyl records are their own gift wrap — the album artwork IS the presentation. Consider adding a personal touch: a handwritten note about why you chose that album, or a card explaining what the music means to you.",
          "For extra impact, pair the record with accessories: a carbon fiber brush, a record cleaning cloth, a vinyl frame for display, or quality inner sleeves. These small additions show you understand the hobby.",
          "Gift a music experience, not just an object: tickets to a record fair, a gift card to their local record store, or a Discogs gift card alongside a physical record creates a multi-layered gift they'll genuinely love.",
        ],
      },
    ],
    quickTips: [
      "Check their Spotify profile for listening data (GoOffline can help!)",
      "Classic albums are safe bets — everyone loves a canon masterpiece on vinyl",
      "Colored vinyl editions feel extra special as gifts",
      "Local record store gift cards let them choose (and discover)",
      "Include a personal note about why you chose that album",
      "CD + turntable fund = great combo for someone new to physical music",
    ],
    ctaText:
      "Know their Spotify playlists? Paste one into GoOffline and get a gift shopping list with prices and buy links. Free and instant.",
  },
  {
    slug: "gen-z",
    persona: "Gen Z Music Fans",
    metaTitle: "Vinyl & CDs for Gen Z: Why Your Generation Is Going Offline",
    metaDescription:
      "Gen Z is driving the physical music revival. Here's why your generation is buying vinyl and CDs, how to start collecting, and why owning music hits different.",
    heroTitle: "Your Generation Gets It",
    heroSubtitle:
      "Gen Z is the first generation to grow up with unlimited streaming — and the first to actively choose physical media. Here's why going offline is the ultimate power move.",
    sections: [
      {
        heading: "Why Gen Z Is Buying Physical Music",
        icon: "📱",
        content: [
          "Your generation was born into digital everything — streaming music, cloud photos, digital downloads. And increasingly, you're pushing back. Vinyl sales to under-25s have surged. CD sales are growing for the first time in two decades, driven largely by Gen Z and K-pop fans.",
          "The reason is intuitive: digital feels disposable. Streaming is great for access, but terrible for ownership. When everything is infinitely available, nothing feels special. Physical media — a record you chose, bought, and display — creates meaning that a playlist can't match.",
          "It's also an aesthetic statement. Vinyl on a shelf is décor. Album covers on a wall are art. Spinning a record is a vibe. In an age of screen fatigue, the analog, tactile experience of physical music is genuinely refreshing.",
        ],
      },
      {
        heading: "Starting Without Breaking the Bank",
        icon: "💰",
        content: [
          "You don't need to be rich to collect. Used CDs cost $1-5 at thrift stores — that's lossless audio you own for the price of a coffee. Even vinyl has affordable entry points: used records start at $5-10, and many new releases cost $25-30.",
          "For equipment, skip the expensive audiophile gear (for now). The Audio-Technica AT-LP60X ($130) is a solid turntable that won't damage your records. Pair it with affordable powered speakers ($80-150) and you're set. Total investment: under $300.",
          "Or start with CDs — you might already have a laptop with a disc drive, or grab a USB CD drive for $15. Rip your CDs to FLAC files and you've got a personal lossless music server. That's the ultimate flex: owning your music AND streaming it, subscription-free.",
        ],
      },
      {
        heading: "The TikTok to Turntable Pipeline",
        icon: "🎵",
        content: [
          "Your music discovery probably starts on TikTok, Instagram Reels, or YouTube Shorts. A snippet goes viral, you add it to Spotify, and maybe that artist becomes a favorite. But the algorithm moves fast — last month's obsession gets buried by next month's viral sound.",
          "Physical music anchors your favorites. When you buy an album on vinyl or CD, you're saying \"this matters to me\" in a permanent way. That Taylor Swift record on your shelf is a statement. That Daft Punk vinyl is a commitment. The music you choose to own defines your taste in a way streaming never will.",
          "Use GoOffline to bridge the gap: paste your Spotify playlist (the one you actually keep going back to) and see what it costs to own those albums. You might be surprised how affordable it is to start building a real collection.",
        ],
      },
      {
        heading: "Building a Collection That Slaps",
        icon: "🔥",
        content: [
          "Start with the albums you return to obsessively — not what's trending, but what genuinely moves you. Your collection should reflect your actual taste, not algorithmic popularity. Five records you love beat fifty you bought for clout.",
          "Mix formats based on budget and preference: vinyl for the albums you want the full experience with, CDs for building out your library affordably. Colored vinyl editions and limited pressings are the collector's equivalent of rare sneakers — but make sure you actually listen to them too.",
          "Document your collection. Photograph your setup, share your finds on socials, and catalog on Discogs. The physical music community is welcoming and passionate — from vinyl TikTok to r/vinyl, you'll find your people.",
        ],
      },
    ],
    quickTips: [
      "Thrift store CDs are the best deal in music — $1-3 for albums you'll own forever",
      "AT-LP60X is the go-to starter turntable (don't buy suitcase players)",
      "Paste your Spotify playlist into GoOffline to find your first buys",
      "Colored vinyl = cool, but sound quality matters more",
      "Support your local record store — they'll help you find great stuff",
      "Rip CDs to FLAC → run your own streaming server → never pay subscription fees again",
    ],
    ctaText:
      "Ready to own your music? Paste your Spotify playlist and see what your favorites cost on vinyl and CD.",
  },
];

/** Lookup map for O(1) persona access by slug */
export const personaBySlug = new Map(personas.map((p) => [p.slug, p]));

/** Get all persona slugs (for sitemap) */
export const getAllPersonaSlugs = (): string[] => personas.map((p) => p.slug);
